import { BACKEND_DATE_FORMAT } from 'config/Vars'
import filter from 'lodash/filter'
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import values from 'lodash/values'
import moment from 'moment'

import { dateToString, stringToDate } from 'modules/core/lib/dates'

import type CalendarEvent from 'modules/calendar/store/models/CalendarEvent'

import { calendarEventCanSeeTimeoffTypes } from 'modules/calendar/lib/calendarLeaves'
import type { CalendarViewEvent } from './types'

function leavePresenter(event: CalendarEvent): CalendarViewEvent {
  const finishOn = event.metadata('finish_on') || dateToString(moment(), 'backend')

  return {
    model: event,
    type: `${event.attributes.type}-${event.leaveType}`,
    title: '',
    start: stringToDate(event.metadata('start_on'), 'backend').toDate(),
    end: stringToDate(finishOn, 'backend').endOf('day').toDate(), // need to be to end of date otherwise react-big-calendar does not count it.
  }
}

function getLeaves(leaves: CalendarEvent[]): CalendarEvent[] {
  return filter(leaves || [], (event) => {
    const approved = event.metadata('approved')

    if (approved === true) return true

    return approved !== false && calendarEventCanSeeTimeoffTypes(event)
  })
}

function processLeaves(leaves: CalendarEvent[]): CalendarViewEvent[] {
  if (leaves.length === 0) return []

  const events = groupBy(leaves, (leave) => leave.attributes.metadata.leave_id)

  return values(mapValues(events, (eventList) => leavePresenter(eventList[0] as CalendarEvent)))
}

type GroupedCalendarEvent = Record<string, CalendarEvent[]>

export interface MunchedData {
  calendarEvents: CalendarViewEvent[]
  leaves: GroupedCalendarEvent
  companyHolidays: GroupedCalendarEvent
  bubbles: GroupedCalendarEvent
}

export default function calendarMuncher(calendarEvents: CalendarEvent[]): MunchedData {
  const eventsByType = groupBy(calendarEvents, (event) => {
    if (event.attributes.type === 'leave') return 'leaves'

    if (event.attributes.type === 'companyHoliday') return 'companyHolidays'

    if (event.attributes.type === 'postEvent' && event.attributes.metadata.starts_at !== null)
      return 'postEvent'

    return 'bubbles'
  })

  const leaves = getLeaves(eventsByType.leaves ?? [])
  const groupByDate = (items: CalendarEvent[]) => groupBy(items, (leave) => leave.attributes.date)
  const groupByLocalizedDate = (items: CalendarEvent[]) =>
    groupBy(items, (leave) =>
      moment(leave.attributes.metadata.starts_at).local().format(BACKEND_DATE_FORMAT)
    )

  const events = groupByLocalizedDate(eventsByType.postEvent || [])
  const bubbles = groupByDate(eventsByType.bubbles || [])
  const eventsAndBubbles = events

  for (const key in bubbles) {
    if (eventsAndBubbles[key] !== undefined) {
      eventsAndBubbles[key] = (eventsAndBubbles[key] || []).concat(bubbles[key] || [])
    } else {
      eventsAndBubbles[key] = bubbles[key] || [] // Provide an empty array as the default value
    }
  }

  return {
    calendarEvents: processLeaves(leaves || []),
    leaves: groupByDate(leaves),
    companyHolidays: groupByDate(eventsByType.companyHolidays || []),
    bubbles: eventsAndBubbles,
  }
}
